/**
 * meta-track.js — first-party Meta tracking (in-house wetracked.io replacement).
 *
 * - Mints/persists _fbp/_fbc (Meta), _ttp (TikTok, stored only), visitor id, click ids, UTMs.
 * - Writes them to Shopify cart attributes (GET /cart/update.js?attributes[...]) with retry,
 *   creating the cart if none exists — so every order webhook carries match data.
 * - Injects the Meta browser pixel (PageView/ViewContent/AddToCart with eventIDs) and mirrors
 *   the same events to the first-party collector (t.trevorfosterstudio.com/e) which forwards
 *   them to the Conversions API — deduplicated by eventID.
 */
(function () {
  'use strict';
  var PIXEL_ID = '2623946261165148';
  var COLLECTOR = 'https://t.trevorfosterstudio.com/e';
  var IDENTIFY = 'https://t.trevorfosterstudio.com/i';
  var w = window, d = document, loc = location;

  function qs(name) {
    try { return new URL(loc.href).searchParams.get(name); } catch (e) { return null; }
  }
  function getCookie(n) {
    var m = d.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(n, v, days) {
    // Root-domain cookie (wetracked's SLD handling, simplified for .com)
    var parts = loc.hostname.split('.');
    var root = parts.slice(-2).join('.');
    d.cookie = n + '=' + encodeURIComponent(v) + ';path=/;max-age=' + days * 86400 +
      ';domain=' + root + ';samesite=lax';
    return v;
  }
  function uuid() {
    if (w.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 3) | 8).toString(16);
    });
  }
  function rand27() {
    var chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var s = '';
    for (var i = 0; i < 27; i++) s += chars.charAt((Math.random() * chars.length) | 0);
    return s;
  }

  // ---- identity capture (formats verified against wetracked's production script) ----
  var FB_RE = /^fb\.\d+\.\d{13}\..+$/;
  var fbclid = qs('fbclid');
  var fbc = fbclid
    ? setCookie('_fbc', 'fb.1.' + Date.now() + '.' + fbclid, 90)
    : getCookie('_fbc');
  if (fbc && fbclid && !FB_RE.test(fbc)) {
    fbc = setCookie('_fbc', 'fb.1.' + Date.now() + '.' + fbclid, 90);
  }
  var fbp = getCookie('_fbp');
  if (!fbp || !FB_RE.test(fbp)) {
    fbp = setCookie('_fbp', 'fb.1.' + Date.now() + '.' + Math.round(0x7fffffff * Math.random()), 90);
  }
  var ttp = getCookie('_ttp') || setCookie('_ttp', rand27(), 90);
  var vid = getCookie('_mt_vid') || setCookie('_mt_vid', uuid().replace(/-/g, ''), 365);
  // Shopify's own 1-year visitor cookie — read-only second identity key, so the
  // graph survives loss of our cookie and joins storefront ↔ checkout pixel.
  var sy = getCookie('_shopify_y') || null;

  var ids = { fbp: fbp, vid: vid, ttp: ttp };
  if (sy) ids.sy = sy;
  if (fbc) ids.fbc = fbc;
  ['ttclid', 'gclid', 'gbraid', 'wbraid', 'msclkid', 'twclid',
   'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'
  ].forEach(function (k) {
    var v = qs(k);
    if (v) ids[k] = v;
  });
  var gclAu = (getCookie('_gcl_au') || '').split('.');
  if (gclAu.length >= 4) ids.auid = gclAu[2] + '.' + gclAu[3];

  // Persist UTMs/click ids across the session (wetracked's localStorage store)
  var LS_KEY = 'mt:attrs';
  try {
    var stored = JSON.parse(localStorage.getItem(LS_KEY)) || {};
    Object.keys(stored).forEach(function (k) { if (!(k in ids)) ids[k] = stored[k]; });
    localStorage.setItem(LS_KEY, JSON.stringify(ids));
  } catch (e) { /* private mode */ }

  // Klaviyo exchange id (their __kla_id trick) — poll until Klaviyo sets it
  function klaviyoKx() {
    var raw = getCookie('__kla_id');
    if (!raw) return null;
    try { return JSON.parse(atob(raw)).$exchange_id || null; } catch (e) { return null; }
  }

  // ---- cart attribute write (GET creates the cart if none exists) ----
  function cartAttrs() {
    var a = {
      host: loc.origin,
      sw: String(w.screen.width),
      sh: String(w.screen.height)
    };
    Object.keys(ids).forEach(function (k) { a[k] = ids[k]; });
    if (w.Shopify) {
      a.locale = w.Shopify.locale || (navigator.language || '').slice(0, 2);
      if (w.Shopify.country) a.country = w.Shopify.country;
    }
    var kx = klaviyoKx();
    if (kx) a.kx = kx;
    return a;
  }
  function writeAttrs(attempt) {
    attempt = attempt || 1;
    var a = cartAttrs();
    var params = Object.keys(a).map(function (k) {
      // Cap values defensively — a runaway utm/click-id param must not blow up
      // the querystring or get the whole write rejected.
      return 'attributes[' + encodeURIComponent(k) + ']=' + encodeURIComponent(String(a[k]).slice(0, 255));
    }).join('&');
    var root = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
    var retry = function () {
      if (attempt < 10) setTimeout(function () { writeAttrs(attempt + 1); }, 1000);
    };
    fetch(root + 'cart/update.js?' + params)
      .then(function (r) {
        // 4xx = Shopify rejected the request — retrying the same payload can't
        // succeed, so don't burn 9 more requests on it.
        if (r.status >= 400 && r.status < 500) return;
        return r.json().then(function (c) {
          if (!(c && c.token)) retry();
        });
      })
      .catch(retry); // network errors / 5xx — transient, retry
  }

  // ---- collector ----
  function send(payload) {
    payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;
    payload.vid = vid;
    if (sy) payload.sy = sy;
    var kx = klaviyoKx();
    if (kx) payload.kx = kx;
    var body = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(COLLECTOR, body)) return;
    } catch (e) { /* fall through */ }
    fetch(COLLECTOR, { method: 'POST', body: body, keepalive: true }).catch(function () {});
  }

  // Identify: hand raw email/phone/name (or pre-hashed `h`) to the worker, which
  // normalizes + SHA-256s and merges into the identity graph under vid/sy/kx.
  // Every later event for this visitor then carries the match keys.
  function identify(fields) {
    var payload = { vid: vid };
    if (sy) payload.sy = sy;
    var kx = klaviyoKx();
    if (kx) payload.kx = kx;
    Object.keys(fields || {}).forEach(function (k) { if (fields[k]) payload[k] = fields[k]; });
    var body = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(IDENTIFY, body)) return;
    } catch (e) { /* fall through */ }
    fetch(IDENTIFY, { method: 'POST', body: body, keepalive: true }).catch(function () {});
  }

  // ---- Meta browser pixel ----
  function loadFbq() {
    if (w.fbq) return;
    var n = (w.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!w._fbq) w._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    var s = d.createElement('script');
    s.async = true;
    s.src = 'https://connect.facebook.net/en_US/fbevents.js';
    var f = d.getElementsByTagName('script')[0];
    f.parentNode.insertBefore(s, f);
  }

  function start() {
    writeAttrs();

    loadFbq();
    w.fbq('init', PIXEL_ID);

    // PageView — browser + server, same eventID (Meta dedups the pair)
    var pageEid = uuid();
    w.fbq('track', 'PageView', {}, { eventID: pageEid });
    send({ n: 'PageView', eid: pageEid, url: loc.href, ref: d.referrer || undefined });

    // ViewContent on product pages (product JSON via the path — wetracked's trick)
    if (/\/products\/[^/]+\/?$/.test(loc.pathname)) {
      fetch(loc.pathname.replace(/\/$/, '') + '.json')
        .then(function (r) { return r.json(); })
        .then(function (j) {
          var p = j && j.product;
          if (!p) return;
          var variantId = String((p.variants[0] || {}).id || p.id);
          var cd = {
            value: +((p.variants[0] || {}).price || 0),
            currency: (w.Shopify && w.Shopify.currency && w.Shopify.currency.active) || 'USD',
            content_ids: [variantId],
            content_name: p.title
          };
          var eid = uuid();
          w.fbq('track', 'ViewContent', cd, { eventID: eid });
          send({ n: 'ViewContent', eid: eid, url: loc.href, cd: cd });
        })
        .catch(function () {});
    }

    // AddToCart — chained fetch wrapper on /cart/add (theme uses fetch everywhere)
    var origFetch = w.fetch;
    w.fetch = function () {
      var url = arguments[0] && (arguments[0].url || arguments[0]);
      var result = origFetch.apply(this, arguments);
      if (typeof url === 'string' && /\/cart\/add(\.js)?/.test(url)) {
        result.then(function (res) {
          if (!res.ok) return;
          res.clone().json().then(function (data) {
            var items = data.items || [data];
            var value = 0, contentIds = [], contents = [], num = 0;
            items.forEach(function (it) {
              if (!it || !it.variant_id) return;
              var price = (it.final_price != null ? it.final_price : it.price) / 100;
              value += price * (it.quantity || 1);
              num += it.quantity || 1;
              contentIds.push(String(it.variant_id));
              contents.push({ id: String(it.variant_id), quantity: it.quantity || 1, item_price: price });
            });
            if (!contentIds.length) return;
            var cd = {
              value: +value.toFixed(2),
              currency: (w.Shopify && w.Shopify.currency && w.Shopify.currency.active) || 'USD',
              content_ids: contentIds,
              contents: contents,
              num_items: num
            };
            var eid = uuid();
            w.fbq('track', 'AddToCart', cd, { eventID: eid });
            send({ n: 'AddToCart', eid: eid, url: loc.href, cd: cd });
          }).catch(function () {});
        }).catch(function () {});
      }
      return result;
    };

    // Lead — Klaviyo signup form submitted (their documented klaviyoForms event).
    // The submitted values ride in e.detail.metaData ($email / $phone_number) —
    // identify() them so this brand-new subscriber matches from now on.
    w.addEventListener('klaviyoForms', function (e) {
      if (!e.detail || e.detail.type !== 'submit') return;
      var eid = uuid();
      w.fbq('track', 'Lead', {}, { eventID: eid });
      send({ n: 'Lead', eid: eid, url: loc.href });
      try {
        var md = e.detail.metaData || {};
        var em = md.$email || md.email;
        var ph = md.$phone_number || md.phone_number;
        if (em || ph) identify({ em: em, ph: ph });
      } catch (err) { /* form payload shape changed — Lead still fired */ }
    });

    // Logged-in customer: theme.liquid exposes pre-hashed em/fn/ln (Liquid sha256 of
    // the trimmed, lowercased values) as window.__mtCust. Once per session is enough —
    // the graph serves every later event.
    try {
      var cust = w.__mtCust;
      if (cust && cust.em && sessionStorage.getItem('mt:cust') !== cust.em) {
        identify({ h: { em: cust.em, fn: cust.fn, ln: cust.ln } });
        sessionStorage.setItem('mt:cust', cust.em);
      }
    } catch (err) { /* no sessionStorage / private mode */ }

    // Klaviyo kx — poll like wetracked, then refresh cart attributes once found
    if (!klaviyoKx()) {
      var tries = 0;
      var timer = setInterval(function () {
        if (klaviyoKx()) { clearInterval(timer); writeAttrs(); }
        else if (++tries > 120) clearInterval(timer);
      }, 1000);
    }
  }

  // ---- consent gate (wetracked's exact logic) ----
  var cp = w.Shopify && w.Shopify.customerPrivacy;
  if (cp && typeof cp.marketingAllowed === 'function' && cp.marketingAllowed() === false) {
    d.addEventListener('visitorConsentCollected', function (e) {
      if (e.detail && e.detail.marketingAllowed !== false) start();
    });
  } else {
    start();
  }
})();
